import { Fragment } from 'react';

import { cn } from '#/utils/cn';
import { DASH } from '#/utils/constants';
import type { Direction } from '#/utils/direction';
import { DIRECTION_TEXT_CLASS, directionOf } from '#/utils/direction';

export interface ValueCompareItem {
  label: string;
  value: number;
}

/**
 * A baseline whose value may not be known. A `null` value still reserves the
 * row — an empty bar and a dash — so the layout doesn't shift when an
 * asynchronous baseline arrives.
 */
export interface ValueCompareBaseline {
  label: string;
  value: number | null;
}

interface ValueCompareProps {
  /** Primary value, rendered first with a green bar. */
  main: ValueCompareItem;
  /**
   * Comparison value, rendered below with a gray bar. When omitted, only the
   * main row is shown and its color reflects the value's own sign rather than a
   * comparison.
   */
  compare?: ValueCompareBaseline;
  /**
   * Formats the value of each value for display. With +/- applied after
   */
  format?: (value: number) => string | undefined;
  className?: string;
}

const defaultFormat = (value: number) => String(value);

const SIGN_PREFIX: Record<Direction, string> = {
  positive: '+',
  negative: '-',
  unchanged: '',
};

function signed(
  value: number,
  format: (value: number) => string | undefined,
): string {
  return `${SIGN_PREFIX[directionOf(value)]}${format(Math.abs(value)) ?? ''}`;
}

const MAIN_BAR_FILL: Record<Direction, string> = {
  positive: 'bg-green-60',
  negative: 'bg-red-60',
  unchanged: 'bg-slate-50',
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

/**
 * Compares two labeled values as stacked proportional bars. The higher of the
 * two values fills the full track; the other fills the fraction it represents
 * of that value — so the smaller (or more negative) value is always the shorter
 * bar, regardless of sign.
 */
export function ValueCompare({
  main,
  compare,
  format = defaultFormat,
  className,
}: ValueCompareProps) {
  // A baseline row may be present with no value yet; it holds space but takes
  // no part in scaling or coloring.
  const baseline = compare?.value ?? null;

  // The higher of the two values anchors the track at full width; every bar is
  // drawn as the fraction it represents of that reference.
  const reference =
    baseline === null ? main.value : Math.max(main.value, baseline);

  // Fraction of the track a value fills. When both values share a sign this is
  // a true ratio — a category at -3% beside a fund at -6% reads as a half bar.
  // A value on the opposite side of zero from the reference can't extend
  // rightward, so it clamps to empty.
  const widthOf = (value: number) => {
    if (reference === 0) {
      return 0;
    }
    const ratio = reference > 0 ? value / reference : reference / value;
    return clampPercent(ratio * 100);
  };

  // With a baseline, color by how main compares to it; without one, fall back
  // to the main value's own sign.
  const direction = directionOf(
    baseline === null ? main.value : main.value - baseline,
  );

  const rows = [
    {
      label: main.label,
      fill: MAIN_BAR_FILL[direction],
      labelClass: 'font-semibold',
      valueClass: DIRECTION_TEXT_CLASS[direction],
      width: widthOf(main.value),
      valueText: signed(main.value, format),
    },
    ...(compare
      ? [
          {
            label: compare.label,
            fill: 'bg-slate-50',
            labelClass: '',
            valueClass: 'text-fg-utility',
            width: baseline === null ? 0 : widthOf(baseline),
            valueText: baseline === null ? DASH : signed(baseline, format),
          },
        ]
      : []),
  ];

  return (
    <div
      className={cn(
        'text-static-14 grid grid-cols-[auto_1fr_auto] items-center gap-x-8 gap-y-12',
        className,
      )}
    >
      {rows.map(({ label, fill, labelClass, valueClass, width, valueText }) => (
        <Fragment key={label}>
          <span className={cn('text-static-14 text-right', labelClass)}>
            {label}
          </span>
          <div
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(width)}
            aria-valuetext={valueText}
            aria-label={label}
            className="bg-steel-30 h-12 w-full overflow-hidden rounded-r-full"
          >
            <div
              className={cn('h-full rounded-r-full', fill)}
              style={{ width: `${width}%` }}
            />
          </div>
          <span className={cn('text-right whitespace-nowrap', valueClass)}>
            {valueText}
          </span>
        </Fragment>
      ))}
    </div>
  );
}
